import * as React from 'react';
import { useRef, useEffect, useCallback } from 'react';
import { HOUR_HEIGHT, UI_COLORS, SNAPS_PER_HOUR, SNAP_HOURS, SNAP_PIXELS } from './calendarConstants';
import useCalendarState from './hooks/useCalendarState';

/**
 * DragSelection - Uses direct DOM manipulation for smooth drag performance
 * Instead of updating React state on every mouse move, we manipulate the DOM directly
 * Supports multi-day selection by tracking start and end day columns
 */
function DragSelection({
  workStartHour = 8,
  workEndHour = 16,
  onDragEnd,
  onDragCancel,
  gridRef,
}) {
  const overlayRef = useRef(null);
  const endDayOverlayRef = useRef(null); // Overlay for end day in multi-day selection
  const dragStateRef = useRef({
    isDragging: false,
    resourceId: null,
    startDayIndex: null,
    endDayIndex: null,
    startY: 0,
    currentY: 0,
    startHour: 0,
    startCellElement: null,
    endCellElement: null,
  });

  const formatTime = (hour) => {
    const h = Math.floor(hour);
    const m = Math.round((hour - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  // Find day column element by index within the same resource row
  const findDayColumn = useCallback((resourceRow, dayIndex) => {
    if (!resourceRow) return null;
    return resourceRow.querySelector(`[data-day-column="${dayIndex}"]`);
  }, []);

  const updateOverlayPosition = useCallback(() => {
    const overlay = overlayRef.current;
    const endOverlay = endDayOverlayRef.current;
    const state = dragStateRef.current;
    if (!overlay || !state.isDragging || !state.startCellElement) return;

    const rowHeight = (workEndHour - workStartHour) * HOUR_HEIGHT;
    const isMultiDay = state.startDayIndex !== state.endDayIndex && state.endDayIndex !== null;

    if (isMultiDay && state.endCellElement) {
      // Multi-day selection
      const startCellRect = state.startCellElement.getBoundingClientRect();
      const endCellRect = state.endCellElement.getBoundingClientRect();

      // Start day: from start hour to end of work day
      const startY = Math.max(0, Math.min(state.startY, rowHeight));
      const startHour = workStartHour + (startY / HOUR_HEIGHT);
      const snappedStartHour = Math.floor(startHour * SNAPS_PER_HOUR) / SNAPS_PER_HOUR;
      const clampedStartHour = Math.max(workStartHour, snappedStartHour);

      // End day: from start of work day to current Y position
      const endY = Math.max(0, Math.min(state.currentY, rowHeight));
      const endHour = workStartHour + (endY / HOUR_HEIGHT);
      const snappedEndHour = Math.max(workStartHour + SNAP_HOURS, Math.ceil(endHour * SNAPS_PER_HOUR) / SNAPS_PER_HOUR);
      const clampedEndHour = Math.min(workEndHour, snappedEndHour);

      // Position start day overlay (from start hour to end of day)
      const startTop = (clampedStartHour - workStartHour) * HOUR_HEIGHT;
      const startHeight = (workEndHour - clampedStartHour) * HOUR_HEIGHT;

      overlay.style.display = 'flex';
      overlay.style.top = `${startCellRect.top + startTop}px`;
      overlay.style.left = `${startCellRect.left + 4}px`;
      overlay.style.width = `${startCellRect.width - 8}px`;
      overlay.style.height = `${Math.max(SNAP_PIXELS, startHeight)}px`;

      // Position end day overlay (from start of day to end hour)
      const endTop = 0;
      const endHeight = (clampedEndHour - workStartHour) * HOUR_HEIGHT;

      endOverlay.style.display = 'flex';
      endOverlay.style.top = `${endCellRect.top + endTop}px`;
      endOverlay.style.left = `${endCellRect.left + 4}px`;
      endOverlay.style.width = `${endCellRect.width - 8}px`;
      endOverlay.style.height = `${Math.max(SNAP_PIXELS, endHeight)}px`;

      // Update time labels
      const startLabel = overlay.querySelector('[data-time-label]');
      const endLabel = endOverlay.querySelector('[data-time-label]');
      if (startLabel) {
        startLabel.textContent = `${formatTime(clampedStartHour)} →`;
      }
      if (endLabel) {
        endLabel.textContent = `→ ${formatTime(clampedEndHour)}`;
      }

      // Store calculated values
      state.calculatedStartHour = clampedStartHour;
      state.calculatedEndHour = clampedEndHour;
    } else {
      // Single-day selection (original logic)
      const cellRect = state.startCellElement.getBoundingClientRect();

      // Calculate hours from Y positions
      const startY = Math.max(0, Math.min(state.startY, rowHeight));
      const currentY = Math.max(0, Math.min(state.currentY, rowHeight));

      const startHour = workStartHour + (startY / HOUR_HEIGHT);
      const endHour = workStartHour + (currentY / HOUR_HEIGHT);

      // Determine actual start/end (allow dragging up or down)
      const actualStartHour = Math.min(startHour, endHour);
      const actualEndHour = Math.max(startHour, endHour);

      // Snap to SNAP_MINUTES increments
      const snappedStart = Math.floor(actualStartHour * SNAPS_PER_HOUR) / SNAPS_PER_HOUR;
      const snappedEnd = Math.max(snappedStart + SNAP_HOURS, Math.ceil(actualEndHour * SNAPS_PER_HOUR) / SNAPS_PER_HOUR);

      const clampedStart = Math.max(workStartHour, snappedStart);
      const clampedEnd = Math.min(workEndHour, snappedEnd);

      // Position overlay
      const top = (clampedStart - workStartHour) * HOUR_HEIGHT;
      const height = (clampedEnd - clampedStart) * HOUR_HEIGHT;

      overlay.style.display = 'flex';
      overlay.style.top = `${cellRect.top + top}px`;
      overlay.style.left = `${cellRect.left + 4}px`;
      overlay.style.width = `${cellRect.width - 8}px`;
      overlay.style.height = `${Math.max(SNAP_PIXELS, height)}px`;

      // Hide end day overlay
      endOverlay.style.display = 'none';

      // Update time label
      const label = overlay.querySelector('[data-time-label]');
      if (label) {
        label.textContent = `${formatTime(clampedStart)} - ${formatTime(clampedEnd)}`;
      }

      // Store calculated hours
      state.calculatedStartHour = clampedStart;
      state.calculatedEndHour = clampedEnd;
    }
  }, [workStartHour, workEndHour]);

  const handleMouseDown = useCallback((e) => {
    // Only the primary button draws a selection; a right press leaves the browser menu alone
    if (e.button !== 0) return;
    // Skip if clicking on an event or resize handle (let DraggableEvent handle it)
    if (e.target.closest('[data-event]') || e.target.closest('[data-resize-handle]')) return;

    // Skip if there's already an event being dragged/resized
    if (useCalendarState.getState().eventDragState) return;

    // Find the day column cell
    const cell = e.target.closest('[data-day-column]');
    if (!cell) return;

    const resourceRow = cell.closest('[data-resource-id]');
    if (!resourceRow) return;

    const resourceId = parseInt(resourceRow.dataset.resourceId, 10);
    const dayIndex = parseInt(cell.dataset.dayColumn, 10);

    const cellRect = cell.getBoundingClientRect();
    const y = e.clientY - cellRect.top;

    dragStateRef.current = {
      isDragging: true,
      resourceId,
      startDayIndex: dayIndex,
      endDayIndex: dayIndex,
      startY: y,
      currentY: y,
      startCellElement: cell,
      endCellElement: cell,
      resourceRow,
      calculatedStartHour: workStartHour,
      calculatedEndHour: workStartHour + 1,
    };

    updateOverlayPosition();

    // Prevent text selection during drag
    e.preventDefault();
  }, [updateOverlayPosition, workStartHour]);

  const handleMouseMove = useCallback((e) => {
    const state = dragStateRef.current;
    if (!state.isDragging || !state.startCellElement) return;

    // Skip if there's an event being dragged/resized (don't interfere)
    if (useCalendarState.getState().eventDragState) return;

    // Check if mouse has moved to a different day column (within same resource row)
    const currentCell = e.target.closest('[data-day-column]');
    if (currentCell) {
      const currentResourceRow = currentCell.closest('[data-resource-id]');
      // Only allow multi-day within same resource row
      if (currentResourceRow === state.resourceRow) {
        const newDayIndex = parseInt(currentCell.dataset.dayColumn, 10);
        // Only allow extending to later days (not earlier)
        if (newDayIndex >= state.startDayIndex) {
          state.endDayIndex = newDayIndex;
          state.endCellElement = currentCell;
          const cellRect = currentCell.getBoundingClientRect();
          state.currentY = e.clientY - cellRect.top;
        }
      }
    } else {
      // Mouse is still in start cell or outside
      const cellRect = state.startCellElement.getBoundingClientRect();
      state.currentY = e.clientY - cellRect.top;
    }

    // Use requestAnimationFrame for smooth updates
    requestAnimationFrame(updateOverlayPosition);
  }, [updateOverlayPosition]);

  const handleMouseUp = useCallback((e) => {
    const state = dragStateRef.current;
    if (!state.isDragging) return;

    // Don't hide overlays here - let them stay visible while context menu/dialog is open
    // They will be hidden by clearDragSelection() when the dialog closes

    // Call the callback with final selection (including multi-day info)
    const isMultiDay = state.startDayIndex !== state.endDayIndex;
    if (onDragEnd && (state.calculatedEndHour > state.calculatedStartHour || isMultiDay)) {
      onDragEnd({
        resourceId: state.resourceId,
        dayIndex: state.startDayIndex,
        startDayIndex: state.startDayIndex,
        endDayIndex: state.endDayIndex,
        startHour: state.calculatedStartHour,
        endHour: state.calculatedEndHour,
        isMultiDay,
        position: { x: e.clientX, y: e.clientY },
      });
    }

    dragStateRef.current = {
      isDragging: false,
      resourceId: null,
      startDayIndex: null,
      endDayIndex: null,
      startY: 0,
      currentY: 0,
      startCellElement: null,
      endCellElement: null,
      resourceRow: null,
    };
  }, [onDragEnd]);

  const handleMouseLeave = useCallback(() => {
    const state = dragStateRef.current;
    if (!state.isDragging) return;

    const overlay = overlayRef.current;
    const endOverlay = endDayOverlayRef.current;
    if (overlay) {
      overlay.style.display = 'none';
    }
    if (endOverlay) {
      endOverlay.style.display = 'none';
    }

    if (onDragCancel) {
      onDragCancel();
    }

    dragStateRef.current = {
      isDragging: false,
      resourceId: null,
      startDayIndex: null,
      endDayIndex: null,
      startY: 0,
      currentY: 0,
      startCellElement: null,
      endCellElement: null,
      resourceRow: null,
    };
  }, [onDragCancel]);

  // Attach event listeners to the grid
  useEffect(() => {
    const grid = gridRef?.current;
    if (!grid) return;

    grid.addEventListener('mousedown', handleMouseDown);
    grid.addEventListener('mousemove', handleMouseMove);
    grid.addEventListener('mouseup', handleMouseUp);
    grid.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      grid.removeEventListener('mousedown', handleMouseDown);
      grid.removeEventListener('mousemove', handleMouseMove);
      grid.removeEventListener('mouseup', handleMouseUp);
      grid.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [gridRef, handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave]);

  // Register a callback with the store so dialogs can hide the overlay when they close
  useEffect(() => {
    const hideOverlays = () => {
      const overlay = overlayRef.current;
      const endOverlay = endDayOverlayRef.current;
      if (overlay) {
        overlay.style.display = 'none';
      }
      if (endOverlay) {
        endOverlay.style.display = 'none';
      }
    };

    useCalendarState.getState().setDragOverlayHideCallback(hideOverlays);

    return () => {
      useCalendarState.getState().setDragOverlayHideCallback(null);
    };
  }, []);

  // Create portal-like fixed overlays (one for start day, one for end day in multi-day)
  return (
    <>
      {/* Start day overlay */}
      <div
        ref={overlayRef}
        style={{
          display: 'none',
          position: 'fixed',
          backgroundColor: UI_COLORS.selectionBg,
          border: `2px dashed ${UI_COLORS.selection}`,
          borderRadius: '8px',
          zIndex: 1000,
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <span
          data-time-label
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: UI_COLORS.selection,
          }}
        />
      </div>
      {/* End day overlay (for multi-day selection) */}
      <div
        ref={endDayOverlayRef}
        style={{
          display: 'none',
          position: 'fixed',
          backgroundColor: UI_COLORS.selectionBg,
          border: `2px dashed ${UI_COLORS.selection}`,
          borderRadius: '8px',
          zIndex: 1000,
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <span
          data-time-label
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: UI_COLORS.selection,
          }}
        />
      </div>
    </>
  );
}

export default DragSelection;
